import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { RootState } from 'StoreModel';
import { useParams } from 'react-router-dom';
import { ApplicationDefinition, ApplicationEntry, Community, FormQuestionAnswer } from 'redux/types/account';
import NewApplication from '../../components/application/new-application';
import styled from 'styled-components';
import { saveApplicationEntryAsync, fetchApplicationEntryByIdAsync } from '../../redux/actions/application-manager';
import { Flag } from '../../redux/types/enums';
import { toast } from '../../components/common/toast';

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const mapStateToProps = (state: RootState) => ({
  literals: state.literals,
  bearer: state.account.session.session.bearer,
  applications: state.account.applications,
  isFetchingApplicationEntry: state.loading.fetchApplicationEntryByIdFlag,
  isSavingApplicationEntry: state.loading.saveApplicationEntryFlag,
});

const mapDispatchToProps = {
  saveApplicationEntry: saveApplicationEntryAsync.request,
  cancelSaveApplicationFlag: saveApplicationEntryAsync.cancel,
  fetchApplicationEntry: fetchApplicationEntryByIdAsync.request,
};

type dispatchType = typeof mapDispatchToProps;

interface Props extends ReturnType<typeof mapStateToProps>, dispatchType {
  applicationDefinition: ApplicationDefinition;
  community: Community;
}

const NewApplicationPage: React.FC<Props> = props => {
  const {
    applicationDefinition,
    community,
    saveApplicationEntry,
    bearer,
    fetchApplicationEntry,
    isFetchingApplicationEntry,
    applications,
    isSavingApplicationEntry,
    cancelSaveApplicationFlag,
    literals,
  } = props;
  const params = useParams<{ applicationDefinitionId: string; applicationEntryId: string }>();
  const applicationEntryId = Number(params.applicationEntryId);

  const handleSaveApplicationEntry = (applicationEntry: ApplicationEntry) => {
    saveApplicationEntry({
      bearer,
      submitApplication: false,
      applicationEntry: {
        ...applicationEntry,
        formAnswers: applicationEntry.formAnswers.map((answer: FormQuestionAnswer) => ({
          ...answer,
          formId: applicationDefinition.formId,
        })),
      },
    });
  };

  const submitApplication = (applicationEntry: ApplicationEntry) => {
    saveApplicationEntry({
      bearer,
      submitApplication: true,
      applicationEntry: {
        ...applicationEntry,
        formAnswers: applicationEntry.formAnswers.map((answer: FormQuestionAnswer) => ({
          ...answer,
          formId: applicationDefinition.formId,
        })),
      },
    });
  };

  useEffect(() => {
    if (applicationEntryId) {
      fetchApplicationEntry({
        bearer,
        applicationEntryId: applicationEntryId,
      });
    }
  }, [applicationEntryId]);

  useEffect(() => {
    if (isSavingApplicationEntry === Flag.Success) {
      toast(literals.application_saved_successfully, { type: 'success' });
      cancelSaveApplicationFlag(null);
    }
  }, [isSavingApplicationEntry]);

  if (isFetchingApplicationEntry === Flag.Request) return null;

  const applicationEntry = (applications || []).find(
    (application: ApplicationEntry) => application.id === applicationEntryId,
  );

  if (!applicationEntry) return null;

  return (
    <React.Fragment>
      <Container>
        <NewApplication
          saveApplicationEntry={handleSaveApplicationEntry}
          defaultApplication={applicationEntry}
          community={community}
          applicationDefinition={applicationDefinition}
          isSavingApplicationEntry={isSavingApplicationEntry}
          submitApplication={submitApplication}
        />
      </Container>
    </React.Fragment>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(NewApplicationPage);
